import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import { RateSupport } from "@components";
import { LoadingPlaceHolder } from "@rentablo/components";
import { Chips } from "@components/filters/Chips/Chips";
import { LayoutWithSidebar } from "@components/common/layouts/LayoutWithSidebar";
import ArrowLeftIcon from "@icons/myicons/arrow-left.svg?react";
import ArrowDown from "@icons/myicons/arrow-down.svg?react";
import { useInvestmentsId } from "@rentablo/api/apiv1";
import { useRecommenderWatchlist } from "../hooks/useRecommenderWatchlist";
import { useRecommenderImages } from "../hooks/useRecommenderImages";
import { DetailsModal } from "./DetailsModal/DetailsModal";
import { RecommenderSidebar } from "./RecommenderSidebar";
import { Recommendation } from "./Recommendation";
import { RecommendationDetailsProvider } from "./DetailsModal/RecommendationDetailsProvider";
import { useRecommendations } from "./hooks/useRecommendations";
import { getTypeChips } from "./utils";

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(6, 6, 14)} !important;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: ${({ theme }) => theme.spacing(4, 4, 12)} !important;
  }
`;

const Wrapper = styled(Box)`
  max-width: 768px;
  width: 100%;
  margin: 0 auto;
`;

export const RecommenderPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const investmentId = searchParams.get("investmentId");
  const {
    recommendations,
    originalFund,
    showLoader,
    visibleRecommendations,
    uniqueTypesCount,
    showMore,
    setShowMore,
    selectedTypes,
    setSelectedTypes,
  } = useRecommendations();
  const {
    watchlistedIsins,
    toggleWatchlistEntry,
    getWatchlistId,
    isLoading: isLoadingWatchlist,
    isSaving: isSavingWatchlist,
  } = useRecommenderWatchlist();

  const { data: investment } = useInvestmentsId(
    {
      id: Number(investmentId),
    },
    {
      enabled: Boolean(investmentId),
    }
  );

  const { images } = useRecommenderImages(recommendations ?? [], investment);
  const [detailsModalData, setDetailsModalData] = useState<{
    recommendation: FundRecommendationResponse;
    view?: "info" | "compare" | "onlyCompare";
  }>();
  const options = getTypeChips();

  const handleGoToWatchlist = async () => {
    const watchlistId = await getWatchlistId();
    navigate(`/watchlists/${watchlistId}`);
  };

  return (
    <>
      <LayoutWithSidebar sidebar={<RecommenderSidebar />}>
        <StyledContainer>
          <Wrapper>
            <Typography variant="h3" mb={6}>
              {t("recommender.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={10}>
              {t("recommender.description", {
                name: originalFund?.name,
              })}
            </Typography>
            {(uniqueTypesCount?.size ?? 0) > 1 && (
              <Chips
                options={options}
                selectedOptions={selectedTypes}
                setSelectedOptions={setSelectedTypes}
                size="medium"
              />
            )}
            <Typography variant="body3" color="text.secondary" mb={4} mt={10}>
              {t("recommender.results", {
                count: visibleRecommendations?.length ?? 0,
              })}
            </Typography>
            <Box display="flex" flexDirection="column" gap={4} mb={8}>
              {showLoader ? (
                <LoadingPlaceHolder />
              ) : (
                visibleRecommendations.map((recommendation) => (
                  <Recommendation
                    key={recommendation.fund.isin}
                    recommendation={recommendation}
                    images={images}
                    onToggleWatchlist={toggleWatchlistEntry}
                    isWatchlisted={
                      watchlistedIsins?.includes(recommendation.fund.isin) ??
                      false
                    }
                    isLoading={isLoadingWatchlist}
                    isSaving={isSavingWatchlist}
                    onOpenDetails={(recommendation, view) =>
                      setDetailsModalData({
                        recommendation,
                        view,
                      })
                    }
                  />
                ))
              )}
            </Box>
            {(recommendations?.length ?? 0) > 3 && (
              <Typography
                onClick={() => setShowMore(!showMore)}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 0.5,
                  mb: 8,
                }}
              >
                {t(`recommender.${showMore ? "showLess" : "showMore"}`)}
                <ArrowDown
                  style={{
                    transform: showMore ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease-in-out",
                  }}
                />
              </Typography>
            )}
            <Box
              display="flex"
              flexDirection={isDownSM ? "column" : "row"}
              justifyContent={isDownSM ? "center" : "space-between"}
              gap={4}
              mb={10}
            >
              <Button
                variant="tertiary"
                startIcon={<ArrowLeftIcon />}
                onClick={() => navigate(`/investments/${investmentId}`)}
              >
                {t("recommender.back")}
              </Button>
              <Button variant="primary" onClick={handleGoToWatchlist}>
                {t("recommender.goToWatchlist")}
              </Button>
            </Box>
            <RateSupport />
          </Wrapper>
        </StyledContainer>
      </LayoutWithSidebar>
      {detailsModalData && (
        <RecommendationDetailsProvider
          originalFund={originalFund}
          recommendation={detailsModalData.recommendation}
          images={images}
          investment={investment}
        >
          <DetailsModal
            onClose={() => setDetailsModalData(undefined)}
            onToggleWatchlist={toggleWatchlistEntry}
            isSavingWatchlist={isSavingWatchlist}
            initialView={detailsModalData.view}
            isWatchlisted={
              watchlistedIsins?.includes(
                detailsModalData.recommendation.fund.isin
              ) ?? false
            }
          />
        </RecommendationDetailsProvider>
      )}
    </>
  );
};
